import { useRef, useEffect, useMemo, useContext } from 'react';
import { VStack } from '@chakra-ui/react';
import { OverviewReportingBundleProvider } from '../../../hooks/reporting/OverviewReportingBundleContext';
import { useWidgetDashboard } from '../widgets/useWidgetDashboard';
import WidgetGrid from '../widgets/WidgetGrid';
import type { WidgetGridHandle } from '../widgets/WidgetGrid';
import type { AuditingDrilldownPayload } from '../widgets/WidgetRenderer';
import type { WidgetSharedParams } from '../widgets/useWidgetData';
import { getReportingComparison } from '../shared/reportingComparisonUtils';
import SessionContext from '../../../contexts/SessionContext';

interface OverviewTabProps {
    startDate: string;
    endDate: string;
    locationId?: string;
    /** When true, hide location-domain widgets (single-location GM). */
    isGm?: boolean;
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    preset?: string;
    isActive?: boolean;
    onRegisterAddWidget?: (fn: () => void) => void;
    onAuditingDrilldown?: (payload: AuditingDrilldownPayload) => void;
}

const OverviewTab = ({
    startDate,
    endDate,
    locationId,
    isGm = false,
    granularity = 'week',
    preset,
    isActive = true,
    onRegisterAddWidget,
    onAuditingDrilldown,
}: OverviewTabProps) => {
    const gridRef = useRef<WidgetGridHandle>(null);
    const sessionContext = useContext(SessionContext);
    // Only prune after session resolves; undefined while loading leaves widgets intact.
    const invoicingEnabled = sessionContext?.isAuthenticated === true
        ? sessionContext.invoicingEnabled === true
        : undefined;

    const { widgets, addWidget, updateWidget, removeWidget, reorderWidgets } = useWidgetDashboard(isGm, {
        invoicingEnabled,
    });

    useEffect(() => {
        if (onRegisterAddWidget) {
            onRegisterAddWidget(() => gridRef.current?.openAddWidget());
        }
    }, [onRegisterAddWidget]);

    const comparison = getReportingComparison(preset);

    const sharedParams: WidgetSharedParams = useMemo(() => ({
        startDate,
        endDate,
        locationId,
        isGm,
        granularity,
        preset,
        comparison,
    }), [startDate, endDate, locationId, isGm, granularity, preset, comparison]);

    return (
        <OverviewReportingBundleProvider
            params={sharedParams}
            widgets={widgets}
            enabled={isActive}
        >
            <VStack spacing={4} align="stretch">
                <WidgetGrid
                    ref={gridRef}
                    widgets={widgets}
                    params={sharedParams}
                    onUpdate={updateWidget}
                    onRemove={removeWidget}
                    onAdd={addWidget}
                    onReorder={reorderWidgets}
                    onAuditingDrilldown={onAuditingDrilldown}
                    isGm={isGm}
                />
            </VStack>
        </OverviewReportingBundleProvider>
    );
};

export default OverviewTab;
